// Helpers de exportação de tabelas (CSV / Excel HTML / PDF print)
// Recebem um descritor genérico e geram ficheiros a transferir no browser.
import { format } from 'date-fns';

const replaceDot = (v, decimals) => {
  if (v === null || v === undefined || v === '') return '';
  const num = Number(v);
  if (Number.isFinite(num)) return num.toFixed(decimals).replace('.', ',');
  return String(v);
};

const formatCell = (col, row) => {
  const value = typeof col.value === 'function' ? col.value(row) : row[col.key];
  if (col.type === 'date' && value) {
    try { return format(new Date(value), 'dd/MM/yyyy'); } catch { return value; }
  }
  if (col.type === 'datetime' && value) {
    try { return format(new Date(value), 'dd/MM/yyyy HH:mm:ss'); } catch { return value; }
  }
  if (col.type === 'number') return replaceDot(value, col.decimals ?? 2);
  if (col.type === 'percent') {
    const num = Number(value);
    if (!Number.isFinite(num)) return '';
    return `${num > 0 ? '+' : ''}${num.toFixed(col.decimals ?? 1).replace('.', ',')}%`;
  }
  if (value === null || value === undefined) return '';
  return String(value);
};

export const exportToCSV = ({ filename, columns, rows, title }) => {
  const headers = columns.map((c) => c.label);
  const lines = rows.map((row) => columns.map((c) => formatCell(c, row)).join(';'));
  const csv = [
    title ? `# ${title}` : null,
    `# Exportado em ${format(new Date(), 'dd/MM/yyyy HH:mm')}`,
    `# Total de registos: ${rows.length}`,
    headers.join(';'),
    ...lines,
  ].filter(Boolean).join('\n');

  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
  link.click();
};

export const exportToExcel = ({ filename, columns, rows, title }) => {
  const headerHtml = columns.map((c) => `<th>${c.label}</th>`).join('');
  const rowsHtml = rows.map((row) => {
    const cells = columns.map((c) => {
      const text = formatCell(c, row);
      const align = c.type === 'number' || c.type === 'percent' ? ' class="number"' : '';
      return `<td${align}>${text}</td>`;
    }).join('');
    return `<tr>${cells}</tr>`;
  }).join('');

  const html = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
    <head><meta charset="UTF-8">
    <style>
      table { border-collapse: collapse; }
      th, td { border: 1px solid #000; padding: 8px; }
      th { background-color: #003D7A; color: white; font-weight: bold; }
      .number { text-align: right; }
    </style>
    </head><body>
      <h2>${title || filename}</h2>
      <p>Data de exportação: ${format(new Date(), 'dd/MM/yyyy HH:mm')}</p>
      <p>Total de registos: ${rows.length}</p>
      <table>
        <thead><tr>${headerHtml}</tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table>
    </body></html>`;

  const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${format(new Date(), 'yyyy-MM-dd')}.xls`;
  link.click();
};

export const exportToPDF = ({ filename, columns, rows, title, subtitle }) => {
  const headerHtml = columns.map((c) => `<th>${c.label}</th>`).join('');
  const rowsHtml = rows.map((row) => {
    const cells = columns.map((c) => {
      const text = formatCell(c, row);
      const cls = c.type === 'number' || c.type === 'percent' ? 'number' : '';
      return `<td class="${cls}">${text}</td>`;
    }).join('');
    return `<tr>${cells}</tr>`;
  }).join('');

  const html = `
    <!DOCTYPE html>
    <html><head><meta charset="UTF-8"><title>${title || filename}</title>
    <style>
      @page { size: A4; margin: 18mm; }
      body { font-family: Arial, sans-serif; font-size: 11px; color: #333; }
      h1 { color: #003D7A; font-size: 18px; margin-bottom: 4px; }
      h2 { color: #666; font-size: 13px; font-weight: normal; margin-top: 0; }
      .meta { color: #666; font-size: 10px; margin-bottom: 14px; }
      table { width: 100%; border-collapse: collapse; }
      th { background-color: #003D7A; color: white; padding: 8px; text-align: left; font-size: 10px; }
      td { padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 10px; }
      tr:nth-child(even) { background-color: #f9fafb; }
      .number { text-align: right; font-family: monospace; }
      .footer { margin-top: 24px; text-align: center; color: #999; font-size: 9px; border-top: 1px solid #e5e7eb; padding-top: 8px; }
      @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
    </style></head><body>
      <h1>${title || filename}</h1>
      ${subtitle ? `<h2>${subtitle}</h2>` : ''}
      <div class="meta">
        Exportado em ${format(new Date(), 'dd/MM/yyyy HH:mm')} · ${rows.length} registos
      </div>
      <table>
        <thead><tr>${headerHtml}</tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table>
      <div class="footer">Infraestruturas de Portugal — Plataforma de Gestão Técnica</div>
      <script>window.onload = () => { window.print(); };</script>
    </body></html>`;

  const printWindow = window.open('', '_blank');
  printWindow.document.write(html);
  printWindow.document.close();
};
